import { io, Socket } from "socket.io-client";
import { logger } from "../utils/logger";
import api from "../config/axios";
import { getAccessToken, setAccessToken } from "../features/auth/services/authTokenStore";
import { Reservation } from "../features/reservations/types/Reservation";
import { ReservaEstacionamiento } from "../types/ReservaEstacionamiento";

type ChangeType = "created" | "updated" | "deleted";
type ClienteChangePayload = Record<string, unknown>;
type ActivityLogCreatedPayload = Record<string, unknown>;
type MailUnreadChangedPayload = {
  unread: number;
  created?: number;
  reason?: string;
};
type SocketAckResponse = { error?: string };
/** ARCA no confirmó el documento de un cliente recién cargado o editado. */
export type ClienteArcaAvisoPayload = {
  usuario_email: string;
  cliente_id: number;
  cliente_nombre: string;
  arca_estado: string;
  reserva_id?: number | null;
};
type ReservaSocketPayload = Partial<Reservation> & {
  reserva_id?: number;
  reserva_data?: Reservation;
};
type ReservaEstacionamientoSocketPayload = Partial<ReservaEstacionamiento> & {
  reserva_id?: number;
  reserva?: ReservaEstacionamiento;
};

interface ChangeEventDetail<TData> {
  type: ChangeType;
  data: TData;
}

class WebSocketService {
  private socket: Socket | null = null;
  private isConnecting = false;
  private readonly clientInstanceId = this.getOrCreateClientInstanceId();

  /**
   * 🔍 Verificar si está en proceso de conexión
   */
  public get connecting(): boolean {
    return this.isConnecting;
  }

  /**
   * 🔍 Obtener la instancia del socket para verificación
   */
  public getSocket(): Socket | null {
    return this.socket;
  }

  constructor() {
    // No conectar automáticamente - conectar solo cuando sea necesario
  }

  private getOrCreateClientInstanceId(): string {
    const storageKey = "gestor679_ws_client_instance_id";
    try {
      const existing = window.sessionStorage.getItem(storageKey);
      if (existing) return existing;

      const generated =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.sessionStorage.setItem(storageKey, generated);
      return generated;
    } catch {
      return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
  }

  /**
   * 🔗 Establecer conexión WebSocket
   */
  public connect() {
    if (this.socket?.connected || this.isConnecting) {
      return;
    }
    // Conectarse pide la sesión de la API: sin ella el servidor rechaza.
    if (!getAccessToken()) {
      return;
    }

    if (this.socket) {
      this.isConnecting = true;
      this.socket.connect();
      return;
    }

    this.isConnecting = true;

    // Determinar URL del backend
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    let backendUrl: string;
    if (hostname === "192.168.13.94") {
      backendUrl = "http://192.168.13.95:5000";
    } else if (hostname === "localhost" || hostname === "127.0.0.1") {
      backendUrl = "http://localhost:5000";
    } else if (hostname === "192.168.12.52") {
      backendUrl = "http://192.168.12.52:5000";
    } else if (hostname === "gestorreservas679inn.com.ar") {
      backendUrl = `${protocol}//${hostname}`;
    } else {
      backendUrl = `${protocol}//${hostname}:5000`;
    }

    logger.debug("Conectando WebSocket", { backendUrl });

    this.socket = io(backendUrl, {
      path: "/socket.io",
      transports: ["polling", "websocket"], // Permitir fallback para mejor compatibilidad
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000, // Timeout mayor para conexiones lentas
      forceNew: false, // ✅ No forzar nueva conexión - reutilizar si existe
      withCredentials: true,
      // Función y no objeto: en cada reconexión manda el token vigente.
      auth: (cb) =>
        cb({
          token: getAccessToken(),
          clientInstanceId: this.clientInstanceId,
          initialPath: window.location.pathname,
        }),
      query: {
        clientInstanceId: this.clientInstanceId,
        initialPath: window.location.pathname,
      },
      // Remover EIO forzado - dejar que se negocie automáticamente
    });

    this.setupEventListeners();
  }

  /**
   * 🎧 Configurar event listeners
   */
  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      logger.info("✅ WebSocket conectado exitosamente");
      this.isConnecting = false;

      // Emitir evento personalizado para notificar a los componentes
      // Usar un pequeño delay para asegurar que los listeners estén listos
      setTimeout(() => {
        const event = new CustomEvent("websocket_connected", {
          detail: { connected: true },
        });
        window.dispatchEvent(event);
        logger.debug("🔍 Evento websocket_connected disparado");
      }, 100);
    });

    this.socket.on("disconnect", (reason) => {
      logger.warn("❌ WebSocket desconectado", { reason });
      this.isConnecting = false;

      // Emitir evento personalizado para notificar a los componentes
      const event = new CustomEvent("websocket_disconnected", {
        detail: { connected: false, reason },
      });
      window.dispatchEvent(event);

      if (reason === "io server disconnect") {
        this.socket?.connect();
      }
    });

    this.socket.on("connect_error", (error) => {
      logger.error("❌ Error de conexión WebSocket", error);
      this.isConnecting = false;
      // Un rechazo del servidor no se reintenta solo (un corte de red sí). Si
      // fue por la sesión vencida, se renueva y se vuelve a conectar.
      if (error.message === "sesion_vencida") {
        void this.renovarSesionYReconectar();
      }
    });

    // 🚀 Eventos específicos de la aplicación
    this.socket.on("reserva_creada", (data) => {
      logger.debug("Nueva reserva creada", { data });
      this.notifyReservaChange("created", data);
    });

    this.socket.on("reserva_actualizada", (data) => {
      logger.debug("Reserva actualizada", { data });
      this.notifyReservaChange("updated", data);
    });

    this.socket.on("reserva_eliminada", (data) => {
      logger.debug("Reserva eliminada", { data });
      this.notifyReservaChange("deleted", data);
    });

    this.socket.on("cliente_eliminado", (data) => {
      logger.debug("Cliente eliminado", { data });
      this.notifyClienteChange("deleted", data);
    });

    this.socket.on("cliente_creado", (data) => {
      logger.debug("Cliente creado", { data });
      this.notifyClienteChange("created", data);
    });

    this.socket.on("cliente_actualizado", (data) => {
      logger.debug("Cliente actualizado", { data });
      this.notifyClienteChange("updated", data);
    });

    this.socket.on("activity_log_created", (data) => {
      logger.debug("Nuevo log de actividad", { data });
      this.notifyActivityLogCreated(data);
    });

    this.socket.on("mail_unread_changed", (data) => {
      logger.debug("Correo no leído actualizado", { data });
      this.notifyMailUnreadChanged(data);
    });

    this.socket.on("cliente_arca_aviso", (data: ClienteArcaAvisoPayload) => {
      window.dispatchEvent(new CustomEvent<ClienteArcaAvisoPayload>("cliente_arca_aviso", { detail: data }));
    });

    // 🚗 Eventos de reservas de estacionamiento
    this.socket.on("reserva_estacionamiento_creada", (data) => {
      logger.debug("Nueva reserva de estacionamiento creada", { data });
      this.notifyReservaEstacionamientoChange("created", data);
    });

    this.socket.on("reserva_estacionamiento_actualizada", (data) => {
      logger.debug("Reserva de estacionamiento actualizada", { data });
      this.notifyReservaEstacionamientoChange("updated", data);
    });

    this.socket.on("reserva_estacionamiento_eliminada", (data) => {
      logger.debug("Reserva de estacionamiento eliminada", { data });
      this.notifyReservaEstacionamientoChange("deleted", data);
    });
  }

  private async renovarSesionYReconectar() {
    try {
      const { data } = await api.post("/auth/refresh");
      if (data?.access_token) {
        setAccessToken(data.access_token);
        this.connect();
      }
    } catch (error) {
      logger.warn("No se pudo renovar la sesión del tiempo real", error);
    }
  }

  /**
   * 📢 Notificar cambios de reserva a los listeners
   */
  private notifyReservaChange(type: ChangeType, data: ReservaSocketPayload) {
    // Emitir evento personalizado para que los componentes puedan escuchar
    const event = new CustomEvent<ChangeEventDetail<ReservaSocketPayload>>(
      "reserva_change",
      {
        detail: { type, data },
      }
    );
    window.dispatchEvent(event);
  }

  /**
   * 📢 Notificar cambios de cliente a los listeners
   */
  private notifyClienteChange(type: ChangeType, data: ClienteChangePayload) {
    // Emitir evento personalizado para que los componentes puedan escuchar
    const event = new CustomEvent<ChangeEventDetail<ClienteChangePayload>>(
      "cliente_change",
      {
        detail: { type, data },
      }
    );
    window.dispatchEvent(event);
  }

  /**
   * 📢 Notificar nuevo movimiento de actividad a los listeners
   */
  private notifyActivityLogCreated(data: ActivityLogCreatedPayload) {
    const event = new CustomEvent<ActivityLogCreatedPayload>(
      "activity_log_created",
      {
        detail: data,
      }
    );
    window.dispatchEvent(event);
  }

  /**
   * 📢 Notificar cambios en correos no leídos
   */
  private notifyMailUnreadChanged(data: MailUnreadChangedPayload) {
    const event = new CustomEvent<MailUnreadChangedPayload>(
      "mail_unread_changed",
      {
        detail: data,
      }
    );
    window.dispatchEvent(event);
  }

  /**
   * 📢 Notificar cambios de reserva de estacionamiento a los listeners
   */
  private notifyReservaEstacionamientoChange(
    type: ChangeType,
    data: ReservaEstacionamientoSocketPayload
  ) {
    // Emitir evento personalizado para que los componentes puedan escuchar
    const event = new CustomEvent<
      ChangeEventDetail<ReservaEstacionamientoSocketPayload>
    >(
      "reserva_estacionamiento_change",
      {
        detail: { type, data },
      }
    );
    window.dispatchEvent(event);
  }

  /**
   * 🎯 Suscribirse a cambios de reservas
   */
  public onReservaChange(
    callback: (type: ChangeType, data: ReservaSocketPayload) => void
  ) {
    // Conectar automáticamente si no está conectado
    if (!this.socket?.connected && !this.isConnecting) {
      this.connect();
    }
    const handleEvent = (event: Event) => {
      const { type, data } = (
        event as CustomEvent<ChangeEventDetail<ReservaSocketPayload>>
      ).detail;
      callback(type, data);
    };

    window.addEventListener("reserva_change", handleEvent);

    // Retornar función para desuscribirse
    return () => {
      window.removeEventListener("reserva_change", handleEvent);
    };
  }

  /**
   * 🎯 Suscribirse a cambios de reservas de estacionamiento
   */
  public onReservaEstacionamientoChange(
    callback: (
      type: ChangeType,
      data: ReservaEstacionamientoSocketPayload
    ) => void
  ) {
    // Conectar automáticamente si no está conectado
    if (!this.socket?.connected && !this.isConnecting) {
      this.connect();
    }
    const handleEvent = (event: Event) => {
      const { type, data } = (
        event as CustomEvent<
          ChangeEventDetail<ReservaEstacionamientoSocketPayload>
        >
      ).detail;
      callback(type, data);
    };

    window.addEventListener("reserva_estacionamiento_change", handleEvent);

    // Retornar función para desuscribirse
    return () => {
      window.removeEventListener("reserva_estacionamiento_change", handleEvent);
    };
  }

  /**
   * 🎯 Suscribirse a cambios de clientes
   */
  public onClienteChange(
    callback: (type: ChangeType, data: ClienteChangePayload) => void
  ) {
    if (!this.socket?.connected && !this.isConnecting) {
      this.connect();
    }
    const handleEvent = (event: Event) => {
      const { type, data } = (
        event as CustomEvent<ChangeEventDetail<ClienteChangePayload>>
      ).detail;
      callback(type, data);
    };

    window.addEventListener("cliente_change", handleEvent);

    return () => {
      window.removeEventListener("cliente_change", handleEvent);
    };
  }

  /**
   * 🎯 Suscribirse a nuevos logs de actividad
   */
  public onActivityLogCreated(
    callback: (data: ActivityLogCreatedPayload) => void
  ) {
    if (!this.socket?.connected && !this.isConnecting) {
      this.connect();
    }
    const handleEvent = (event: Event) => {
      callback((event as CustomEvent<ActivityLogCreatedPayload>).detail);
    };

    window.addEventListener("activity_log_created", handleEvent);

    return () => {
      window.removeEventListener("activity_log_created", handleEvent);
    };
  }

  /**
   * Suscribirse a los avisos de ARCA sobre clientes cargados o editados
   */
  public onClienteArcaAviso(callback: (data: ClienteArcaAvisoPayload) => void) {
    const handleEvent = (event: Event) => {
      callback((event as CustomEvent<ClienteArcaAvisoPayload>).detail);
    };

    window.addEventListener("cliente_arca_aviso", handleEvent);

    return () => {
      window.removeEventListener("cliente_arca_aviso", handleEvent);
    };
  }

  /**
   * 🎯 Suscribirse a cambios del contador de correo
   */
  public onMailUnreadChanged(
    callback: (data: MailUnreadChangedPayload) => void
  ) {
    if (!this.socket?.connected && !this.isConnecting) {
      this.connect();
    }
    const handleEvent = (event: Event) => {
      callback((event as CustomEvent<MailUnreadChangedPayload>).detail);
    };

    window.addEventListener("mail_unread_changed", handleEvent);

    return () => {
      window.removeEventListener("mail_unread_changed", handleEvent);
    };
  }

  /**
   * 🔗 Verificar estado de conexión
   */
  public isConnected(): boolean {
    const connected = this.socket?.connected || false;
    logger.debug("WebSocket isConnected check", {
      connected,
      socketExists: !!this.socket,
      isConnecting: this.isConnecting,
    });

    // NO conectar automáticamente aquí - puede causar loops infinitos
    // La conexión debe ser manejada explícitamente
    return connected;
  }

  /**
   * 🔄 Reconectar manualmente
   */
  public reconnect() {
    this.disconnect();
    this.connect();
  }

  /**
   * 🔌 Desconectar
   */
  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
  }

  /**
   * 💓 Ping para verificar conexión
   */
  public ping(): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error("No conectado"));
        return;
      }

      const start = Date.now();
      this.socket.emit("ping", (_response: unknown) => {
        const latency = Date.now() - start;
        resolve(latency);
      });

      // Timeout después de 5 segundos
      setTimeout(() => reject(new Error("Timeout")), 5000);
    });
  }

  /**
   * 🎯 Emit personalizado con retry
   */
  public emit(event: string, data: unknown): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error("No conectado"));
        return;
      }

      this.socket.emit(event, data, (response?: SocketAckResponse) => {
        if (response?.error) {
          reject(new Error(response.error));
        } else {
          resolve();
        }
      });
    });
  }
}

// Exportar instancia singleton
export const websocketService = new WebSocketService();
export default websocketService;
